import React from 'react';
import moment from 'moment-timezone';
import _ from 'underscore';
import { Col, Row, ButtonToolbar, Button, Alert } from 'react-bootstrap';
import { cloneDeep } from 'lodash';
import { titleize } from 'underscore.string';
import Container from 'components/v2/container';
import MARKETS from 'constants/Markets';
import Store from 'stores/ReportingStore';
import {
  getDailyGrowthMetrics,
  getDailyCustomerMetrics,
  getDailyLookbackMetrics,
  getFrequentShippers,
} from 'actions/ReportingActionCreators';
import { withTranslation } from 'react-i18next';
import DatePicker from '../../time/datepicker';
import DailyGrowthMetricTable from './DailyGrowthMetricTable';
import ReportingTable from './ReportingTable';
import { snakify } from '../../../utils/localization/utils';

moment.tz.setDefault('America/Los_Angeles');

const shingleify = (term) => {
  return snakify(
    term.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()),
  );
};

function getStateFromStore() {
  return {
    metrics: Store.getDailyReportMetrics(),
    frequentShippers: Store.getFrequentShippers(),
  };
}

function getDefaultFilters() {
  const markets = {};
  _.each(MARKETS, (market) => {
    markets[market.value] = false;
  });

  return {
    market: markets,
    channel: {
      insideSales: false,
      enterpriseSales: false,
    },
    shipmentType: {
      drayage: false,
      shorthaul: false,
      longhaul: false,
      project: false,
      dayrate: false,
    },
    customerType: {
      frequentShipper: false,
    },
    businessType: {
      newStart: false,
      existing: false,
    },
  };
}

class DailyReport extends React.Component {
  constructor() {
    super();
    this.state = this.getInitialState();
  }

  getInitialState() {
    const initialDateMoment = moment().endOf('day');
    const initialDate = initialDateMoment.toDate();
    return {
      filters: getDefaultFilters(),
      selectedDate: initialDate,
      loading: true,
      data: getStateFromStore(),
    };
  }

  componentDidMount() {
    Store.addChangeListener(this.onChange.bind(this));
    this.getMetrics(this.state.selectedDate, this.state.filters);
    getFrequentShippers(this.state.selectedDate);
  }

  componentWillUnmount() {
    Store.removeChangeListener(this.onChange);
  }

  getMetrics(date, filters) {
    this.setState({ loading: true });

    if (filters.customerType.frequentShipper) {
      filters.shipperId = this.state.data.frequentShippers;
    } else {
      delete filters.shipperId;
    }

    getDailyGrowthMetrics(date, filters);
    getDailyCustomerMetrics(date, filters);
    getDailyLookbackMetrics(date, filters);
  }

  onChange() {
    this.setState({
      loading: false,
      data: getStateFromStore(),
    });
  }

  onDateChanged(date) {
    const dateMoment = moment(date);

    this.setState((prevState) => {
      return {
        ...prevState,
        selectedDate: dateMoment.toDate(),
      };
    });

    this.getMetrics(dateMoment.toDate(), this.state.filters);
  }

  filter = (name, setting) => () => {
    this.setState(
      (prevState) => {
        const filters = cloneDeep(prevState.filters);
        const filterSettings = filters[name];

        if (setting) {
          filterSettings[setting] = !filterSettings[setting];
        } else {
          Object.keys(filterSettings).forEach((filter) => {
            filterSettings[filter] = false;
          });
        }

        filters[name] = filterSettings;
        return {
          ...prevState,
          filters,
        };
      },
      () => {
        this.getMetrics(this.state.selectedDate, this.state.filters);
      },
    );
  };

  buildFilterButtons(name, localizationRequired = true) {
    const filters = this.state.filters[name];
    const { t } = this.props;
    let buttons = _(filters).map((value, setting) => {
      return (
        <Button
          key={setting}
          bsStyle={value ? 'success' : 'default'}
          onClick={this.filter(name, setting)}
          active={value}
        >
          {localizationRequired ? titleize(t(shingleify(setting))) : setting}
        </Button>
      );
    });

    const noFilter = !_.compact(_.values(filters)).length;

    const all = (
      <Button
        key="all"
        bsStyle={noFilter ? 'success' : 'default'}
        onClick={this.filter(name)}
        active={noFilter}
      >
        {titleize(t('ALL'))}
      </Button>
    );

    buttons.push(all);

    return buttons;
  }

  getSalesChannel() {
    const channels = [];
    const { t } = this.props;
    Object.keys(this.state.filters.channel).forEach((ch) => {
      if (this.state.filters.channel[ch]) {
        channels.push(ch.slice(0, ch.length - 5).toUpperCase());
      }
    });
    const salesType =
      !channels.length ||
      channels.length === Object.keys(this.state.filters.channel).length
        ? 'ALL'
        : channels.join(', ');
    return titleize(t(salesType + '_SALES'));
  }

  getShipmentType() {
    const { t } = this.props;
    const shipmentTypes = [];
    Object.keys(this.state.filters.shipmentType).forEach((type) => {
      if (this.state.filters.shipmentType[type]) {
        shipmentTypes.push(titleize(t(shingleify(type))));
      }
    });

    const types =
      !shipmentTypes.length ||
      shipmentTypes.length ===
        Object.keys(this.state.filters.shipmentType).length
        ? titleize(t('ALL'))
        : shipmentTypes.join(', ');
    return types + ' ' + t('SHIPMENT', { count: 2 });
  }

  getCustomerType() {
    const { t } = this.props;
    if (this.state.filters.customerType.frequentShipper)
      return t('FREQUENT_SHIPPERS');
    return t('ALL_CUSTOMERS');
  }

  getSelectedMarket() {
    const markets = [];
    const { t } = this.props;
    _.each(MARKETS, (market) => {
      if (this.state.filters.market[market.value]) {
        markets.push(market.value);
      }
    });

    const selected =
      !markets.length ||
      markets.length === Object.keys(this.state.filters.market).length
        ? titleize(t('ALL'))
        : markets.join(', ');
    const mkAmnt = t('MARKET', { count: markets.length });
    return `${selected} ${mkAmnt}`;
  }

  getSelectedDate() {
    return moment(this.state.selectedDate).format('LL');
  }

  getSummaryTableTitle() {
    const { t } = this.props;
    return (
      this.getSelectedMarket() +
      ', ' +
      this.getCustomerType() +
      ', ' +
      this.getSalesChannel() +
      ', ' +
      this.getShipmentType() +
      ' - ' +
      t('ON') +
      ' ' +
      this.getSelectedDate() +
      ' ' +
      t('TIMEZONE_NAME')
    );
  }

  getCustomerData(customerType, periodName) {
    if (this.state.data.metrics[customerType]) {
      return this.state.data.metrics[customerType][periodName] || [];
    }
    return [];
  }

  getDailyGrowthMetrics() {
    return this.state.data.metrics.growth || [];
  }

  getLookbackMetrics() {
    return this.state.data.metrics.lookback || [];
  }

  render() {
    const state = this.state;
    const { t } = this.props;
    const customerSummaryHeaders = [
      titleize(t('CUSTOMER')),
      titleize(t('JOB', { count: 2 })),
      t('PERCENT_OF_JOBS'),
      t('REVENUE'),
      t('PERCENT_OF_REVENUE'),
    ];
    let loading = '';

    const marketButtons = this.buildFilterButtons('market', false);
    const salesChannelButtons = this.buildFilterButtons('channel');
    const shipmentTypeButtons = this.buildFilterButtons('shipmentType');
    const customerTypeButtons = this.buildFilterButtons('customerType');
    const businessTypeButtons = this.buildFilterButtons('businessType');

    if (this.state.loading) {
      loading = <Alert bsStyle="warning">{t('RELOADING')}</Alert>;
    }

    return (
      <Container>
        <div className="mt-10">
          <Row>
            <Col sm={2}>
              <h2>{t('DAILY_REPORT')}</h2>
            </Col>
            <Col sm={3}>
              <DatePicker
                label={t('SELECT_A_DAY')}
                initialDate={new Date()}
                maxDate={new Date()}
                onChange={this.onDateChanged.bind(this)}
              />
            </Col>
            <Col sm={3}>
              <label>{titleize(t('MARKET', { count: 1 }))}</label>
              <ButtonToolbar>{marketButtons}</ButtonToolbar>
            </Col>
            <Col sm={4}>{loading}</Col>
          </Row>
          <hr />
          <Row>
            <Col sm={3}>
              <label>{t('SALES_CHANNEL')}</label>
              <ButtonToolbar>{salesChannelButtons}</ButtonToolbar>
            </Col>
            <Col sm={3}>
              <label>{t('SHIPMENT_TYPE')}</label>
              <ButtonToolbar>{shipmentTypeButtons}</ButtonToolbar>
            </Col>
            <Col sm={3}>
              <label>{t('CUSTOMER_TYPE')}</label>
              <ButtonToolbar>{customerTypeButtons}</ButtonToolbar>
            </Col>
            <Col sm={3}>
              <label>{t('NEW_BUSINESS')}</label>
              <ButtonToolbar>{businessTypeButtons}</ButtonToolbar>
            </Col>
          </Row>
          <hr />
          <Row>
            <Col sm={8}>
              <DailyGrowthMetricTable
                title={this.getSummaryTableTitle()}
                data={this.getDailyGrowthMetrics()}
              />
            </Col>
            <Col sm={4}>
              <ReportingTable
                title={t('RECENT_DAYS')}
                headers={[t('DATE', {count: 1}), t('JOB', { count: 2 })]}
                initialData={this.getLookbackMetrics()}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={4}>
              <ReportingTable
                title={t('SHIPPERS_TODAY')}
                headers={customerSummaryHeaders}
                initialData={this.getCustomerData('shippers', 'Today')}
              />
            </Col>
            <Col sm={4}>
              <ReportingTable
                title={t('SHIPPERS_WTD')}
                headers={customerSummaryHeaders}
                initialData={this.getCustomerData('shippers', 'WTD')}
              />
            </Col>
            <Col sm={4}>
              <ReportingTable
                title={t('SHIPPERS_MTD')}
                headers={customerSummaryHeaders}
                initialData={this.getCustomerData('shippers', 'MTD')}
              />
            </Col>
          </Row>
          <Row>
            <Col sm={4}>
              <ReportingTable
                title={t('CARRIERS_TODAY')}
                headers={customerSummaryHeaders}
                initialData={this.getCustomerData('carriers', 'Today')}
              />
            </Col>
            <Col sm={4}>
              <ReportingTable
                title={t('CARRIERS_WTD')}
                headers={customerSummaryHeaders}
                initialData={this.getCustomerData('carriers', 'WTD')}
              />
            </Col>
            <Col sm={4}>
              <ReportingTable
                title={t('CARRIERS_MTD')}
                headers={customerSummaryHeaders}
                initialData={this.getCustomerData('carriers', 'MTD')}
              />
            </Col>
          </Row>
        </div>
      </Container>
    );
  }
}

export default withTranslation()(DailyReport);
